import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import paymentService from '../../api/services/paymentService';
import { handleApiError, formatCurrency } from '../../utils/helpers';

const PaymentVerifyPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [pendingPaymentData, setPendingPaymentData] = useState(null);

  // Get reference from URL query params (Paystack sends this)
  const reference = searchParams.get('reference') || searchParams.get('trxref');

  useEffect(() => {
    console.log('=== PaymentVerifyPage Mounted ===');
    console.log('Current URL:', window.location.href);
    console.log('Reference from URL:', reference);

    // Retrieve stored payment data from sessionStorage
    const storedData = sessionStorage.getItem('pendingPayment');
    console.log('Stored payment data:', storedData);
    
    if (storedData) {
      try {
        setPendingPaymentData(JSON.parse(storedData));
      } catch (e) {
        console.error('Failed to parse stored payment data:', e);
      }
    }

    // Verify payment if reference exists
    if (reference) {
      verifyPayment(reference);
    } else {
      console.error('No payment reference found in URL');
      setError('No payment reference found in URL. Please try again.');
      setVerifying(false);
    }
  }, [reference]);

  const verifyPayment = async (ref) => {
    try {
      console.log('Starting payment verification for reference:', ref);
      setVerifying(true);
      setError(null);

      const response = await paymentService.verifyPayment(ref);
      
      console.log('=== FULL VERIFICATION RESPONSE ===');
      console.log(JSON.stringify(response, null, 2));
      
      // Check if verification was successful
      if (response && response.success) {
        console.log('Payment verification successful!');
        setPaymentData(response);
        
        // Clear stored payment data after successful verification
        sessionStorage.removeItem('pendingPayment');
      } else {
        console.error('Payment verification returned unsuccessful response');
        setError(response?.message || 'Payment verification failed');
      }
    } catch (err) {
      console.error('=== PAYMENT VERIFICATION ERROR ===');
      console.error('Error:', err);
      setError(handleApiError(err));
    } finally {
      setVerifying(false);
    }
  };

  // Check if payment is completed
  const isPaymentCompleted = () => {
    if (!paymentData) return false;
    
    // Check multiple possible locations for payment status
    return (
      paymentData.success === true ||
      paymentData.payment?.status === 'COMPLETED' ||
      paymentData.data?.payment?.status === 'COMPLETED' ||
      paymentData.status === 'COMPLETED'
    );
  };

  // Get contest ID from response or stored data
  const getContestId = () => {
    return (
      paymentData?.vote?.contestId ||
      paymentData?.data?.vote?.contestId ||
      paymentData?.contestId ||
      pendingPaymentData?.contestId
    );
  };

  // Get vote count
  const getVoteCount = () => {
    return (
      paymentData?.vote?.voteCount ||
      paymentData?.data?.vote?.voteCount ||
      pendingPaymentData?.voteCount ||
      0
    );
  };

  // Get contestant name
  const getContestantName = () => {
    return (
      paymentData?.vote?.contestant?.name ||
      paymentData?.data?.vote?.contestant?.name ||
      pendingPaymentData?.contestantName ||
      'N/A'
    );
  };

  // Get payment amount
  const getAmount = () => {
    return (
      paymentData?.payment?.amount ||
      paymentData?.data?.payment?.amount ||
      paymentData?.amount ||
      pendingPaymentData?.amount
    );
  };

  // Loading State
  if (verifying) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader size="lg" text="Verifying your payment..." />
            {pendingPaymentData && (
              <div className="mt-8 bg-white rounded-lg shadow-md p-6 max-w-sm mx-auto">
                <p className="text-sm text-gray-500 mb-2">Processing payment for</p>
                <p className="font-bold text-xl text-gray-900 mb-3">
                  {pendingPaymentData.contestantName}
                </p>
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm">
                    {pendingPaymentData.voteCount} vote{pendingPaymentData.voteCount > 1 ? 's' : ''}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm font-semibold text-red-600">
                    {formatCurrency(pendingPaymentData.amount)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-xl p-8 text-center">
            
            {/* Error State */}
            {error ? (
              <>
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
                  <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Payment Verification Failed
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">{error}</p>
                
                <div className="space-y-3">
                  {reference && (
                    <Button 
                      variant="primary" 
                      fullWidth 
                      onClick={() => verifyPayment(reference)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Try Again
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    fullWidth 
                    onClick={() => navigate('/contests')}
                  >
                    Back to Contests
                  </Button>
                </div>
              </>
            ) : isPaymentCompleted() ? (
              <>
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6 animate-pulse">
                  <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Payment Successful!
                </h2>
                <p className="text-gray-600 mb-8 text-lg">
                  Your vote has been recorded successfully.
                </p>

                {/* Payment Details Card */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-8 text-left border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                    Payment Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600 text-sm">Reference</span>
                      <span className="font-mono text-sm text-gray-900 font-medium">
                        {reference}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600 text-sm">Contestant</span>
                      <span className="font-semibold text-gray-900">
                        {getContestantName()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600 text-sm">Votes Cast</span>
                      <span className="font-semibold text-gray-900">
                        {getVoteCount()} vote{getVoteCount() > 1 ? 's' : ''}
                      </span>
                    </div>
                    {getAmount() && (
                      <div className="flex justify-between items-center py-3 bg-white rounded-lg px-3 mt-3">
                        <span className="text-gray-700 font-medium">Amount Paid</span>
                        <span className="font-bold text-xl text-green-600">
                          {formatCurrency(getAmount())}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="primary"
                    fullWidth
                    // In the "View Contest Results" button (around line 221):
// onClick={() => {
//   const contestId = getContestId();
  
//   // Store payment completion info
//   sessionStorage.setItem('pendingPayment', JSON.stringify({
//     contestId: contestId,
//     contestantName: getContestantName(),
//     voteCount: getVoteCount(),
//     timestamp: Date.now()
//   }));
  
//   if (contestId) {
//     navigate(`/contests/${contestId}`);
//   } else {
//     navigate('/contests');
//   }
// }}
                    onClick={() => {
                      const contestId = getContestId();
                      if (contestId) {
                        navigate(`/contests/${contestId}`);
                      } else {
                        navigate('/contests');
                      }
                    }}
                    className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-lg py-3"
                  >
                    View Contest Results
                  </Button>
                  <Button 
                    variant="outline" 
                    fullWidth 
                    onClick={() => navigate('/contests')}
                  >
                    Browse More Contests
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-yellow-100 mb-6">
                  <svg className="h-10 w-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Payment Status Unknown
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  We're unable to confirm your payment status at this time. If you completed the payment, your vote will be counted shortly.
                </p>

                {/* Debug Info - Shows the actual response structure */}
                {paymentData && (
                  <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left">
                    <p className="text-xs text-gray-500 mb-2 font-semibold">Response Data (for debugging):</p>
                    <pre className="text-xs text-gray-700 overflow-auto max-h-48 whitespace-pre-wrap break-words">
                      {JSON.stringify(paymentData, null, 2)}
                    </pre>
                  </div>
                )}
                
                <div className="space-y-3">
                  <Button 
                    variant="primary" 
                    fullWidth 
                    onClick={() => verifyPayment(reference)}
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    Check Status Again
                  </Button>
                  <Button 
                    variant="outline" 
                    fullWidth 
                    onClick={() => navigate('/contests')}
                  >
                    Back to Contests
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PaymentVerifyPage;